/**
 * Platform Admin: глобальні довідники необоротних активів (рішення власника
 * 2026-08-27 — «винести на спільну платформу»): групи активів (ПКУ 138.3)
 * і методи амортизації. Таблиці без tenant_id — спільні для всіх юзерів.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  platformAssetRefs, type PlatformAssetGroup, type PlatformDepreciationMethod,
} from '@/api/assetsRegistry';
import DataTable, { type ColumnDef } from '@/shared/components/DataTable';

const errText = (e: unknown) => {
  const d = (e as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
  return typeof d === 'string' ? d : JSON.stringify(d ?? String(e));
};

export function AssetGroupsTab() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['platform-asset-groups'], queryFn: platformAssetRefs.groups });
  const [edit, setEdit] = useState<PlatformAssetGroup | null>(null);
  const [isNew, setIsNew] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const done = () => { qc.invalidateQueries({ queryKey: ['platform-asset-groups'] }); setEdit(null); };
  const save = useMutation({
    mutationFn: (g: PlatformAssetGroup) => isNew
      ? platformAssetRefs.createGroup({
          code: g.code, asset_kind: g.asset_kind, group_number: g.group_number, name_uk: g.name_uk,
          min_useful_life_months: g.min_useful_life_months,
          accounting_account_code: g.accounting_account_code,
          depreciation_account_code: g.depreciation_account_code, is_active: g.is_active,
        })
      : platformAssetRefs.updateGroup(g.id, {
          name_uk: g.name_uk, min_useful_life_months: g.min_useful_life_months,
          accounting_account_code: g.accounting_account_code,
          depreciation_account_code: g.depreciation_account_code, is_active: g.is_active,
        }),
    onSuccess: done, onError: (e) => setErr(errText(e)),
  });
  const cols: ColumnDef<PlatformAssetGroup>[] = [
    { key: 'asset_kind', header: 'Вид', width: 80, cell: (r) => r.asset_kind === 'FIXED_ASSET' ? 'ОЗ' : 'НМА' },
    { key: 'group_number', header: '№', width: 50, cell: (r) => r.group_number, sortValue: (r) => r.group_number },
    { key: 'code', header: 'Код', width: 110, cell: (r) => r.code },
    { key: 'name_uk', header: 'Назва', cell: (r) => r.name_uk },
    { key: 'min_useful_life_months', header: 'Мін. строк, міс', width: 120, align: 'right', cell: (r) => r.min_useful_life_months ?? '' },
    { key: 'accounting_account_code', header: 'Рах. обліку', width: 100, cell: (r) => r.accounting_account_code ?? '' },
    { key: 'depreciation_account_code', header: 'Рах. зносу', width: 100, cell: (r) => r.depreciation_account_code ?? '' },
    { key: 'is_active', header: 'Активна', width: 80, cell: (r) => r.is_active ? '✓' : '' },
  ];
  return (
    <div>
      <p style={{ color: 'var(--muted)', fontSize: '0.85rem' }}>
        Групи ОЗ (ПКУ ст. 138.3.3) та НМА (138.3.4) — спільні для всіх організацій платформи.
        Мінімальний строк — податкова межа; рахунки — за планом рахунків.
      </p>
      <div className="toolbar"><button className="primary" onClick={() => {
        setErr(null); setIsNew(true);
        setEdit({ id: '', code: '', asset_kind: 'FIXED_ASSET', group_number: 1, name_uk: '',
          min_useful_life_months: null, accounting_account_code: null,
          depreciation_account_code: null, is_active: true, seed_source: null });
      }}>+ Нова група</button></div>
      <DataTable rows={q.data ?? []} columns={cols} rowKey={(r) => r.id} loading={q.isLoading}
        onRowDoubleClick={(r) => { setErr(null); setIsNew(false); setEdit({ ...r }); }}
        rowMuted={(r) => r.is_active ? undefined : 0.5} />
      {edit && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 1100, background: 'var(--overlay)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          onClick={() => setEdit(null)}>
          <div className="card" style={{ width: 520, margin: 0 }} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ marginTop: 0 }}>{isNew ? 'Нова група' : `Група: ${edit.name_uk}`}</h3>
            <GroupForm g={edit} isNew={isNew} onChange={setEdit} />
            {err && <div style={{ color: '#dc2626', marginBottom: 8 }}>{err}</div>}
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button className="primary" disabled={save.isPending || !edit.code || !edit.name_uk}
                onClick={() => save.mutate(edit)}>зберегти</button>
              <button onClick={() => setEdit(null)}>скасувати</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function GroupForm({ g, isNew, onChange }: { g: PlatformAssetGroup; isNew: boolean; onChange: (g: PlatformAssetGroup) => void }) {
  const set = (patch: Partial<PlatformAssetGroup>) => onChange({ ...g, ...patch });
  return (
    <>
      <div className="doc-grid">
        <div className="field" style={{ gridColumn: 'span 4' }}><label>Вид</label>
          <select value={g.asset_kind} disabled={!isNew} onChange={(e) => set({ asset_kind: e.target.value as never })}>
            <option value="FIXED_ASSET">ОЗ</option><option value="INTANGIBLE">НМА</option></select></div>
        <div className="field" style={{ gridColumn: 'span 4' }}><label>№ групи</label>
          <input type="number" min="1" value={g.group_number} disabled={!isNew} onChange={(e) => set({ group_number: Number(e.target.value) })} /></div>
        <div className="field" style={{ gridColumn: 'span 4' }}><label>Код</label>
          <input value={g.code} disabled={!isNew} onChange={(e) => set({ code: e.target.value })} /></div>
      </div>
      <div className="field"><label>Назва</label><input value={g.name_uk} onChange={(e) => set({ name_uk: e.target.value })} /></div>
      <div className="doc-grid">
        <div className="field" style={{ gridColumn: 'span 4' }}><label>Мін. строк, міс</label>
          <input type="number" min="0" value={g.min_useful_life_months ?? ''} onChange={(e) => set({ min_useful_life_months: e.target.value === '' ? null : Number(e.target.value) })} /></div>
        <div className="field" style={{ gridColumn: 'span 4' }}><label>Рахунок обліку</label>
          <input value={g.accounting_account_code ?? ''} onChange={(e) => set({ accounting_account_code: e.target.value || null })} /></div>
        <div className="field" style={{ gridColumn: 'span 4' }}><label>Рахунок зносу</label>
          <input value={g.depreciation_account_code ?? ''} onChange={(e) => set({ depreciation_account_code: e.target.value || null })} /></div>
      </div>
      <label style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 8 }}>
        <input type="checkbox" checked={g.is_active} onChange={(e) => set({ is_active: e.target.checked })} /> активна
      </label>
    </>
  );
}

export function DepreciationMethodsTab() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['platform-depr-methods'], queryFn: platformAssetRefs.methods });
  const [edit, setEdit] = useState<PlatformDepreciationMethod | null>(null);
  const [isNew, setIsNew] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const done = () => { qc.invalidateQueries({ queryKey: ['platform-depr-methods'] }); setEdit(null); };
  const save = useMutation({
    mutationFn: (m: PlatformDepreciationMethod) => isNew
      ? platformAssetRefs.createMethod({ code: m.code, name_uk: m.name_uk, description: m.description, is_active: m.is_active })
      : platformAssetRefs.updateMethod(m.id, { name_uk: m.name_uk, description: m.description, is_active: m.is_active }),
    onSuccess: done, onError: (e) => setErr(errText(e)),
  });
  const cols: ColumnDef<PlatformDepreciationMethod>[] = [
    { key: 'code', header: 'Код', width: 200, cell: (r) => r.code },
    { key: 'name_uk', header: 'Назва', cell: (r) => r.name_uk },
    { key: 'description', header: 'Опис', cell: (r) => r.description ?? '' },
    { key: 'is_active', header: 'Активний', width: 90, cell: (r) => r.is_active ? '✓' : '' },
  ];
  return (
    <div>
      <p style={{ color: 'var(--muted)', fontSize: '0.85rem' }}>
        Методи нарахування амортизації (П(С)БО 7 п. 26) — спільні для всіх організацій.
        Неактивний метод не пропонується у картках активів.
      </p>
      <div className="toolbar"><button className="primary" onClick={() => {
        setErr(null); setIsNew(true);
        setEdit({ id: '', code: '', name_uk: '', description: null, is_active: true, seed_source: null });
      }}>+ Новий метод</button></div>
      <DataTable rows={q.data ?? []} columns={cols} rowKey={(r) => r.id} loading={q.isLoading}
        onRowDoubleClick={(r) => { setErr(null); setIsNew(false); setEdit({ ...r }); }} rowMuted={(r) => r.is_active ? undefined : 0.5} />
      {edit && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 1100, background: 'var(--overlay)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          onClick={() => setEdit(null)}>
          <div className="card" style={{ width: 480, margin: 0 }} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ marginTop: 0 }}>{isNew ? 'Новий метод' : `Метод: ${edit.code}`}</h3>
            <div className="field"><label>Код</label>
              <input value={edit.code} disabled={!isNew} onChange={(e) => setEdit({ ...edit, code: e.target.value })} /></div>
            <div className="field"><label>Назва</label>
              <input value={edit.name_uk} onChange={(e) => setEdit({ ...edit, name_uk: e.target.value })} /></div>
            <div className="field"><label>Опис</label>
              <input value={edit.description ?? ''} onChange={(e) => setEdit({ ...edit, description: e.target.value || null })} /></div>
            <label style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 8 }}>
              <input type="checkbox" checked={edit.is_active} onChange={(e) => setEdit({ ...edit, is_active: e.target.checked })} /> активний
            </label>
            {err && <div style={{ color: '#dc2626', marginBottom: 8 }}>{err}</div>}
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button className="primary" disabled={save.isPending || !edit.code || !edit.name_uk} onClick={() => save.mutate(edit)}>зберегти</button>
              <button onClick={() => setEdit(null)}>скасувати</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
